import * as React from 'react';
import { Stack, Typography, Grid, Box } from '@mui/material';
import { Card } from 'primereact/card';
import { COMMUNICATION_TYPE_OPTIONS, COMMUNICATION_TEMPLATE_OPTIONS } from "./common";
import dayjs from "dayjs";
import {COMMUNICATION_TYPE} from "../../service/communicationService";
import { UserTypes } from "../../enum/UserTypes";

interface CommunicationHistoryInfoProps {
  session: {
    user_type: string;
  };
  historyData: {
    history: {
      id: string;
      client_id: string;
      employee_id: string;
      type: number;
      template: number;
      template_parameters: any;
      content_id: string;
      external_tracking_id: string;
      created_at: string;
      sent_at: string;
      send_success: boolean;
      external_failed_status: string | null;
      to: string;
      client_name: string;
      employee_full_name: string;
    };
    content: {
      id: string;
      content: string;
    };
    billing_usage?: {
      client_id: string;
      usage_type: number;
      usage_id: string;
      quantity: number;
      cost_system: number;
      cost_client: number;
      created_at: string;
      billing_cycle_month_date: string;
      id: string;
      pricing_id: number;
    };
  };
}

export default function CommunicationHistoryInfo({ historyData, session }: CommunicationHistoryInfoProps) {
  const { history, content, billing_usage } = historyData;

  const getTypeLabel = (type: number) => {
    const option = COMMUNICATION_TYPE_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  const getTemplateLabel = (template: number) => {
    const option = COMMUNICATION_TEMPLATE_OPTIONS.find(opt => opt.value === template);
    return option ? option.label : template;
  };

  const renderContent = () => {
    if (history.type === COMMUNICATION_TYPE.EMAIL) {
      return <div dangerouslySetInnerHTML={{ __html: content.content }} />;
    } else { // SMS or WhatsApp
      return content.content.split(/\\r\\n|\\n|\n|\r/).map((line, i) => (
        <React.Fragment key={i}>
          {line}
          <br />
        </React.Fragment>
      ));
    }
  };

  const formatDateTime = (date: string) => {
    return dayjs(date).format('YYYY/MM/DD HH:mm:ss');
  };

  const renderTemplateParameters = () => {
    if (!history.template_parameters || Object.keys(history.template_parameters).length === 0) {
      return '-';
    }

    return Object.entries(history.template_parameters).map(([key, value]) => (
      <div key={key} style={{marginLeft: '5px'}}>
        - {key}: {value as string}
      </div>
    ));
  };

  return (
    <Box sx={{ p: 2}}>
      <Grid container spacing={3}>
        {/* Left Column - Content */}
        <Grid item xs={7}>
          <Card>
            <Typography variant="h4" gutterBottom>Content</Typography>
            <div style={{
              height: 'calc(100vh - 200px)',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              {renderContent()}
            </div>
          </Card>
        </Grid>

        {/* Right Column - Info Blocks */}
        <Grid item xs={5}>
          <Stack spacing={3}>
            {/* Communication Details Section */}
            <Card>
              <Typography variant="h4" gutterBottom>Communication Details</Typography>
              <Stack spacing={1}>
                <div><strong>To:</strong> {history.to}</div>
                <div><strong>Client:</strong> {history.client_name || "SYSTEM"}</div>
                <div><strong>Employee:</strong> {history.employee_full_name}</div>

                <div><strong>Type:</strong> {getTypeLabel(history.type)}</div>
                <div><strong>Template:</strong> {getTemplateLabel(history.template)}</div>
                <div><strong>Template Parameters:</strong> {renderTemplateParameters()}</div>

                <div><strong>Created At:</strong> {formatDateTime(history.created_at)}</div>
                <div><strong>Sent At:</strong> {formatDateTime(history.sent_at)}</div>
                <div><strong>Status:</strong> {history.send_success ? 'Success' : 'Failed'}</div>
              </Stack>
            </Card>

            {billing_usage && (
              <Card>
                <Typography variant="h4" gutterBottom>Billing Information</Typography>
                <Stack spacing={1}>
                  <div><strong>Quantity:</strong> {billing_usage.quantity}</div>
                  {billing_usage.cost_system && session.user_type === UserTypes.DSF && (
                    <div><strong>System Cost:</strong> R{billing_usage.cost_system.toFixed(2)}</div>
                  )}
                  <div><strong>Client Cost:</strong> R{billing_usage.cost_client.toFixed(2)}</div>
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
